/**
 * AdminSendingDomains Page
 *
 * GitHub issue "[Email] Move cold outreach off the brand domain —
 * dedicated, warmed, rotated sending domains" (#188/#516), milestone
 * "Email & Outreach Overhaul".
 *
 * The dedicated warmed sending domains (`getherohire.org`,
 * `hqherohire.org`, `ideaherohire.com`, `joinherohire.com`) already exist
 * in Instantly.ai at 100% health — the remaining scope this page covers
 * is VISIBILITY: confirm SPF/DKIM/DMARC are correct for each, show which
 * domains are actively in cold-outreach rotation, and make it obvious
 * that the brand domain (`herohire.ai`) is reserved for transactional/
 * login mail and is never part of that rotation.
 *
 * Demo AC: "Open the sending-domains admin view → the active rotation
 * lists the warmed domains with per-domain SPF/DKIM/DMARC status shown
 * as 'pass'".
 *
 * Fetches `GET /api/admin/sending-domains`
 * (`apps/workers/src/api/admin/sendingDomains.ts`).
 *
 * RBAC: mounted behind `AdminGuard` (see `router.tsx`) — only
 * `is_superuser` users can reach this route; the API endpoint itself
 * independently re-verifies superuser status server-side.
 *
 * Related: apps/workers/src/lib/sendingDomainAuthCheck.ts,
 * apps/workers/src/lib/instantlyMailboxPool.ts (BRAND_DOMAIN),
 * app/routes/admin/CustomerHealth.tsx (sibling admin page this one's
 * layout/loading/error conventions mirror).
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAdminSendingDomains, reportApiError } from '@/lib/api/apiClient';
import { Skeleton } from '@/components/ui/skeleton';
import type { SendingDomainAuthTriState } from '@/lib/validation/sendingDomains';

const AUTH_STATUS_BADGE: Record<SendingDomainAuthTriState, { label: string; className: string }> = {
  pass: { label: 'pass', className: 'bg-green-100 text-green-800' },
  fail: { label: 'fail', className: 'bg-red-100 text-red-800' },
  unknown: { label: 'unknown', className: 'bg-gray-100 text-gray-700' },
};

function AuthBadge({ value }: { value: SendingDomainAuthTriState }) {
  const badge = AUTH_STATUS_BADGE[value];
  return (
    <span
      data-testid="auth-check-badge"
      data-status={value}
      className={`px-2 py-1 text-xs font-semibold rounded-full ${badge.className}`}
    >
      {badge.label}
    </span>
  );
}

export default function AdminSendingDomains() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch, isFetching } = useAdminSendingDomains();

  useEffect(() => {
    if (isError) {
      reportApiError(error, 'Failed to load sending domains');
    }
  }, [isError, error]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md">
          <div className="text-lg font-semibold text-destructive">Error Loading Sending Domains</div>
          <p className="text-sm text-muted-foreground mt-2">
            {error instanceof Error ? error.message : 'Failed to fetch sending domains'}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="mt-4 px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const activeRotationRows = data.data.filter((row) => row.in_active_rotation);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-2xl font-bold">Sending Domains</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Dedicated, warmed cold-outreach domains — SPF/DKIM/DMARC verification + active rotation.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50"
              >
                {isFetching ? 'Refreshing…' : 'Refresh now'}
              </button>
              <button
                onClick={() => navigate('/admin')}
                className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium"
              >
                Back to Dashboard
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span data-testid="sending-domains-summary">
              {data.active_rotation_count} domain{data.active_rotation_count === 1 ? '' : 's'} in active rotation
            </span>
            <span>Generated at {new Date(data.generated_at).toLocaleString()}</span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-8">
        <section>
          <h2 className="text-lg font-semibold mb-3">Active rotation</h2>
          {activeRotationRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No domains are currently in active rotation — every domain either failed its SPF/DKIM/DMARC check or
              has not been checked yet.
            </p>
          ) : (
            <div className="overflow-x-auto border border-border rounded-lg">
              <table className="w-full text-sm" data-testid="active-rotation-table">
                <thead className="border-b border-border bg-muted/50">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold">Domain</th>
                    <th className="px-4 py-2 text-left font-semibold">SPF</th>
                    <th className="px-4 py-2 text-left font-semibold">DKIM</th>
                    <th className="px-4 py-2 text-left font-semibold">DMARC</th>
                    <th className="px-4 py-2 text-left font-semibold">Overall</th>
                    <th className="px-4 py-2 text-left font-semibold">Last checked</th>
                  </tr>
                </thead>
                <tbody>
                  {activeRotationRows.map((row) => (
                    <tr
                      key={row.domain}
                      data-testid="sending-domain-row"
                      data-domain={row.domain}
                      data-in-active-rotation="true"
                      className="border-b border-border hover:bg-muted/50"
                    >
                      <td className="px-4 py-2 font-mono">{row.domain}</td>
                      <td className="px-4 py-2">
                        <AuthBadge value={row.spf} />
                      </td>
                      <td className="px-4 py-2">
                        <AuthBadge value={row.dkim} />
                      </td>
                      <td className="px-4 py-2">
                        <AuthBadge value={row.dmarc} />
                      </td>
                      <td className="px-4 py-2">
                        <AuthBadge value={row.auth_check_status} />
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {row.auth_checked_at ? new Date(row.auth_checked_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">All sending identities</h2>
          <p className="text-sm text-muted-foreground mb-3">
            Includes the brand domain (<span className="font-mono">herohire.ai</span>) for reference — it is
            reserved for transactional/login mail only and is never eligible for cold-outreach rotation.
          </p>
          <div className="overflow-x-auto border border-border rounded-lg">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Domain</th>
                  <th className="px-4 py-2 text-left font-semibold">Status</th>
                  <th className="px-4 py-2 text-left font-semibold">Overall auth check</th>
                  <th className="px-4 py-2 text-left font-semibold">In active rotation?</th>
                  <th className="px-4 py-2 text-left font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((row) => (
                  <tr
                    key={row.domain}
                    data-testid="all-sending-domain-row"
                    data-domain={row.domain}
                    data-brand-domain={row.is_brand_domain}
                    className="border-b border-border hover:bg-muted/50"
                  >
                    <td className="px-4 py-2 font-mono">
                      {row.domain}
                      {row.is_brand_domain && (
                        <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
                          brand domain
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">{row.status}</td>
                    <td className="px-4 py-2">
                      <AuthBadge value={row.auth_check_status} />
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          row.in_active_rotation ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {row.in_active_rotation ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{row.excluded_reason ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
